/**
 * Schema và chuẩn hoá dữ liệu "ứng dụng".
 *
 * Dữ liệu đến từ hai nguồn (Firebase và seed local) với nhiều thế hệ khác nhau,
 * nên mọi bản ghi đều đi qua `normalizeApp` trước khi được UI dùng tới.
 */

import { DEFAULT_STATUS } from '../constants/appMeta';

/** Bản ghi rỗng dùng cho form "thêm ứng dụng mới". */
export const emptyApp = () => ({
    id: '',
    title: '',
    tagline: '',
    description: '',
    longDescription: '',
    path: '',
    category: '',
    platform: 'Web',
    status: DEFAULT_STATUS,
    tags: [],
    features: [],
    techStack: [],
    date: new Date().toISOString().slice(0, 7),
    thumbnail: null,
    images: [],
    selfHosted: false,
    externalUrl: '',
    githubUrl: '',
    downloadUrl: '',
    videoUrl: '',
    docsUrl: '',
    hidden: false,
    featured: false,
    order: 0,
});

const toArray = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === 'string' && value.trim()) {
        return value.split(',').map((item) => item.trim()).filter(Boolean);
    }
    return [];
};

/**
 * Chuẩn hoá một bản ghi ứng dụng:
 * - đảm bảo `images` luôn là mảng và `thumbnail` luôn có nếu có ảnh
 * - suy ra `githubUrl` từ `externalUrl` cũ nếu link trỏ tới GitHub
 * - suy ra `selfHosted` cho các bản ghi cũ (khi đó "không có link ngoài" nghĩa là chạy nội bộ)
 * - điền giá trị mặc định cho các trường mới thêm về sau
 */
export const normalizeApp = (raw) => {
    if (!raw) return null;

    const images = toArray(raw.images);
    if (!images.length && raw.thumbnail) images.push(raw.thumbnail);

    let externalUrl = raw.externalUrl || '';
    let githubUrl = raw.githubUrl || '';

    // Dữ liệu đời đầu để link GitHub trong externalUrl — tách ra cho đúng ngữ nghĩa.
    if (!githubUrl && externalUrl.includes('github.com')) {
        githubUrl = externalUrl;
        externalUrl = '';
    }

    const path = raw.path || raw.id || '';
    const downloadUrl = raw.downloadUrl || '';
    const selfHosted =
        raw.selfHosted !== undefined
            ? Boolean(raw.selfHosted)
            : Boolean(path) && !externalUrl && !githubUrl && !downloadUrl;

    return {
        ...raw,
        id: raw.id || raw.path || raw.key || '',
        path,
        title: raw.title || 'Chưa đặt tên',
        tagline: raw.tagline || '',
        description: raw.description || '',
        longDescription: raw.longDescription || '',
        category: raw.category || 'Khác',
        platform: raw.platform || 'Web',
        status: raw.status || DEFAULT_STATUS,
        tags: toArray(raw.tags),
        features: toArray(raw.features),
        techStack: toArray(raw.techStack),
        date: raw.date || '',
        images,
        thumbnail: raw.thumbnail || images[0] || null,
        selfHosted,
        externalUrl,
        githubUrl,
        downloadUrl,
        videoUrl: raw.videoUrl || '',
        docsUrl: raw.docsUrl || '',
        hidden: Boolean(raw.hidden),
        featured: Boolean(raw.featured),
        order: Number.isFinite(raw.order) ? raw.order : 0,
    };
};

/**
 * Ứng dụng chạy nội bộ (nằm trong thư mục public) — mở được bằng iframe
 * ngay trên site thay vì phải sang tên miền khác.
 */
export const isSelfHosted = (app) => Boolean(app?.selfHosted && app?.path);

/** URL để "mở ứng dụng": link ngoài, hoặc trang chạy nội bộ. */
export const getLaunchUrl = (app) => {
    if (!app) return null;
    if (app.externalUrl) return app.externalUrl;
    if (isSelfHosted(app)) return `/${app.path}/index.html`;
    return app.githubUrl || app.downloadUrl || null;
};

/** Trang giới thiệu của ứng dụng. */
export const getDetailPath = (app) => `/app/${app.id}`;

/**
 * Sắp xếp: nổi bật lên trước, sau đó theo `order`, cuối cùng theo ngày mới nhất.
 */
export const sortApps = (apps) =>
    [...apps].sort((a, b) => {
        if (a.featured !== b.featured) return a.featured ? -1 : 1;
        if (a.order !== b.order) return b.order - a.order;
        return (b.date || '').localeCompare(a.date || '');
    });

/** Kiểm tra dữ liệu form trước khi lưu. Trả về object lỗi theo từng field. */
export const validateApp = (form) => {
    const errors = {};
    if (!form.id?.trim()) errors.id = 'Cần có mã định danh (slug)';
    else if (!/^[a-z0-9-]+$/.test(form.id.trim())) errors.id = 'Chỉ dùng chữ thường, số và dấu gạch ngang';
    if (!form.title?.trim()) errors.title = 'Cần có tên ứng dụng';
    if (!form.description?.trim()) errors.description = 'Cần có mô tả ngắn';
    if (!form.category?.trim()) errors.category = 'Cần chọn danh mục';
    const hasLink = [form.externalUrl, form.githubUrl, form.downloadUrl].some((url) => url?.trim());
    if (!hasLink && !form.selfHosted) {
        errors.externalUrl = 'Cần ít nhất một liên kết, hoặc bật "chạy nội bộ"';
    }
    return errors;
};
